import React, { useContext, useState, useEffect } from 'react'
import {
  ButtonsDivFlush,
  CloseDiv,
  CloseImg,
  ErrorMessage,
  HeaderDiv,
  SkillChip,
  SkillChipRemoveButton,
  SkillChipsWrap,
  TipText,
} from './style'
import {
  Container,
  CreateButtonDiv,
  CreateButtonText,
  HeaderText,
  InputDiv,
  InputField,
  LabelCreate,
  ModalActionsColumn,
  SelectField,
} from '../EditAccessUserModal/style'
import cross from '../../assets/addStockModal/cross.svg'
import { Save } from 'lucide-react'
import ModalContext, { SkillOption } from '../../contexts/ModalContext'
import axios from '../../config/axios'
import { useProfileContext } from '../../contexts/AccessesContext'
import UserAttachmentSection, { type AttachmentData } from '../UserAttachmentSection'
import InfiniteSelect from '../InfiniteSelect'
import ApiService from '../../services/ApiService'

const CreateAccessUserModal: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    company: '',
    cnpj: '',
    profileId: '',
  })
  const { setModalActive } = useContext(ModalContext)
  const { profiles } = useProfileContext()
  const [attachments, setAttachments] = useState<AttachmentData[]>([])
  const [selectedSkills, setSelectedSkills] = useState<SkillOption[]>([])
  const [skillInputKey, setSkillInputKey] = useState(0)
  const [error, setError] = useState('')
  const [click, setClick] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchTenantData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user/tenant`)
        console.log('✅ Dados da tenant:', response.data)

        if (response.data) {
          const company = response.data.companyName || ''
          const cnpj = (response.data.cnpj || '').replace(/[^\d]/g, '')

          console.log('📦 Company:', company, 'CNPJ:', cnpj)

          setFormData(prev => ({
            ...prev,
            company,
            cnpj,
          }))
        }
      } catch (error) {
        console.error('❌ Erro ao buscar dados da tenant:', error)
        setError('Erro ao carregar dados da empresa')
      } finally {
        setLoading(false)
      }
    }

    fetchTenantData()
  }, [])

  const validateForm = () => {
    if (!formData.name || formData.name.length < 3) return false
    if (!formData.email || !formData.email.includes('@')) return false
    if (!formData.password || formData.password.length < 6) return false
    if (formData.phone.trim() && formData.phone.trim().length < 10) return false
    return true
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setError('')
  }

  const createUser = async () => {
    console.log('🚀 Iniciando criação de usuário...')
    console.log('📝 FormData atual:', formData)

    if (!validateForm()) {
      console.log('❌ Validação falhou')
      setClick(true)
      return
    }

    if (!formData.company || !formData.cnpj) {
      console.log('⚠️ Company ou CNPJ vazios:', { company: formData.company, cnpj: formData.cnpj })
      setError('Carregando dados da empresa...')
      return
    }

    try {
      const userData = {
        ...formData,
        profileId: formData.profileId ? Number(formData.profileId) : null,
        adminUser: false,
        role: 'TENANT_USER',
        picture: '',
        approved: 'pending',
        permissions: [],
      }

      const formDataPayload = new FormData()
      Object.entries(userData).forEach(([k, v]) => {
        if (v === null || v === undefined) {
          formDataPayload.append(k, '')
        } else if (Array.isArray(v) || typeof v === 'object') {
          formDataPayload.append(k, JSON.stringify(v))
        } else {
          formDataPayload.append(k, String(v))
        }
      })
      attachments.forEach((a) => {
        if (a.file) formDataPayload.append('files', a.file)
      })
      formDataPayload.append(
        'filesMetadata',
        JSON.stringify(attachments.map((a) => ({ title: a.title }))),
      )

      console.log('📤 Enviando dados do usuário:', userData)
      const { data: created } = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user`, formDataPayload)
      console.log('✅ Usuário criado com sucesso!')
      if (selectedSkills.length > 0) {
        await ApiService.skill.updateUserSkills(
          created.id,
          selectedSkills.map((s) => s.id),
        )
      }
      setModalActive(false)
      window.location.reload()
    } catch (error: any) {
      console.error('❌ Erro ao criar usuário:', error)
      if (error.response?.status === 409) {
        setError('Usuário com este email já existe')
      } else if (error.response?.status === 400) {
        const messages = error.response?.data?.message
        if (Array.isArray(messages)) {
          setError(messages.join(', '))
        } else {
          setError('Dados inválidos. Verifique os campos obrigatórios.')
        }
      } else {
        setError('Erro ao criar usuário')
      }
    }
  }

  const isValid = validateForm()

  return (
    <Container>
      <HeaderDiv>
        <HeaderText>Cadastrar novo usuário</HeaderText>
        <CloseDiv onClick={() => setModalActive(false)}>
          <CloseImg src={cross} alt="Fechar" />
        </CloseDiv>
      </HeaderDiv>

      {loading && (
        <p style={{ margin: '0.75rem 0 0', fontSize: '0.88rem', color: '#696969' }}>
          Carregando dados...
        </p>
      )}
      {error && <ErrorMessage>{error}</ErrorMessage>}

      <InputDiv>
        <LabelCreate>Nome completo *</LabelCreate>
        <InputField
          placeholder='Digite o nome completo'
          type='text'
          value={formData.name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('name', e.target.value)}
        />
      </InputDiv>

      <InputDiv>
        <LabelCreate>Email *</LabelCreate>
        <InputField
          placeholder='Digite o email'
          type='email'
          value={formData.email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('email', e.target.value)}
        />
      </InputDiv>

      <InputDiv>
        <LabelCreate>Senha *</LabelCreate>
        <InputField
          placeholder='Digite a senha (mínimo 6 caracteres)'
          type='password'
          value={formData.password}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('password', e.target.value)}
        />
      </InputDiv>

      <InputDiv>
        <LabelCreate>Telefone</LabelCreate>
        <InputField
          placeholder='Digite o telefone'
          type='text'
          value={formData.phone}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('phone', e.target.value)}
        />
      </InputDiv>

      <InputDiv>
        <LabelCreate>Perfil de acesso (opcional)</LabelCreate>
        <SelectField
          value={formData.profileId}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleInputChange('profileId', e.target.value)}
        >
          <option value="">Sem perfil</option>
          {profiles.map((profile) => (
            <option key={profile.id} value={profile.id}>
              {profile.name}
            </option>
          ))}
        </SelectField>
      </InputDiv>

      <InputDiv>
        <LabelCreate>Habilidades</LabelCreate>
        <InfiniteSelect
          key={skillInputKey}
          mode="crud"
          label=""
          placeholder="Buscar ou criar habilidade..."
          endpointConfig={{
            baseUrl: process.env.REACT_APP_GATEWAY_BASE_URL,
            listPath: '/backend/skills',
            createPath: '/backend/skills',
            searchParam: 'search',
            limitParam: 'take',
            offsetParam: 'skip',
          }}
          allowCreate={true}
          allowDelete={false}
          showCreateOnlyIfNotExists={true}
          showSelectedBelow={false}
          value={null}
          onSelect={(skill) => {
            if (skill && !selectedSkills.find((s) => s.id === skill.id)) {
              setSelectedSkills((prev) => [...prev, { id: skill.id, name: skill.name }])
            }
            setSkillInputKey((k) => k + 1)
          }}
        />
        {selectedSkills.length > 0 && (
          <SkillChipsWrap>
            {selectedSkills.map((skill) => (
              <SkillChip key={skill.id}>
                {skill.name}
                <SkillChipRemoveButton
                  type="button"
                  onClick={() =>
                    setSelectedSkills((prev) => prev.filter((s) => s.id !== skill.id))
                  }
                  aria-label={`Remover ${skill.name}`}
                >
                  ×
                </SkillChipRemoveButton>
              </SkillChip>
            ))}
          </SkillChipsWrap>
        )}
      </InputDiv>

      <UserAttachmentSection
        attachments={attachments}
        setAttachments={setAttachments}
      />

      <ModalActionsColumn>
        <ButtonsDivFlush>
          {!isValid && click && (
            <TipText>Preencha todos os campos obrigatórios corretamente</TipText>
          )}
          <CreateButtonDiv onClick={createUser}>
            <Save width={16} height={16} />
            <CreateButtonText>Cadastrar usuário</CreateButtonText>
          </CreateButtonDiv>
        </ButtonsDivFlush>
      </ModalActionsColumn>
    </Container>
  )
}

export default CreateAccessUserModal
